import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="19615" className="hidden md:flex gap-6 items-center">
        {[1, 2, 3].map(i => <div key={i} className="h-5 w-20 bg-muted/40 animate-pulse rounded-sm" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn("group inline-flex h-9 w-max items-center justify-center rounded-sm px-2 py-2 text-[11px] font-sans tracking-widest uppercase transition-all hover:text-primary focus:text-primary outline-none disabled:pointer-events-none disabled:opacity-50", "text-foreground/70 hover:bg-transparent focus:bg-transparent")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-40 bg-background/98 backdrop-blur-2xl md:hidden flex flex-col pt-32 px-6 animate-in fade-in slide-in-from-top-5 duration-300">
      <nav className="flex flex-col gap-8 items-center text-center">
        {loading ? <div className="flex flex-col gap-6 w-full items-center">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-10 w-40 bg-muted/40 animate-pulse rounded-sm" data-index={i} />)}
          </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-3xl font-serif text-foreground/90 hover:text-primary transition-colors tracking-tight" onClick={onClose} data-index={i}>
              {item.label}
            </Link>)}
        <div className="mt-12 w-full max-w-xs">
          <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 text-sm font-sans uppercase tracking-widest rounded-sm" asChild>
            <Link to="/ContactPage" onClick={onClose}>
              Start Organizing
            </Link>
          </Button>
        </div>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  return <header id="main-navigation" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b", isScrolled ? "bg-background/80 backdrop-blur-md border-secondary/30 shadow-sm" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 flex items-center justify-between !w-[2000px] !h-[90px]">
        {}
        <div className="flex-shrink-0 z-50">
          <Link to="/HomePage" className="block">
            <img src="https://stunning-sculpin.10web.cloud/wp-content/uploads/2026/03/Minimalist-house-and-leaf-logo.png" alt="The Organized Space" className="object-contain grayscale transition-opacity !rounded-2xl !m-0 !h-[65px] !w-[100px] hover:opacity-100" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="40" className="flex">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex items-center">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-sans text-xs uppercase tracking-widest px-8 rounded-sm h-11 transition-all" asChild>
            <Link to="/ContactPage">Start Organizing</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden flex items-center z-50">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-secondary/20" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-5 w-5 stroke-[1.5]" /> : <Menu className="h-5 w-5 stroke-[1.5]" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <MenuProvider menu_id="40" className="md:hidden">
          <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>}
    </header>;
}